// House math for the AI-curated "Costs incurred" card (CostsPanel + the
// dossier export's Cost summary). The CURATION (grouping, labels,
// categories) is AI and renders with AI marks; every NUMBER shown is
// computed here in integer cents — the AI-claimed total_usd is never
// displayed.
//
// Rules (Round 5.1):
// - All arithmetic in integer cents (no float accumulation).
// - If the curated set contains an exact component/total relationship,
//   the total item FOLDS: it stays listed (badged), but is excluded from
//   the sums so nothing is double-counted (same shared findExactFold the
//   financial summary uses).
// - The headline renders ONLY when it verifies: if an item DESCRIBES
//   itself as a total ("total", "sum", "overall", "grand") but does NOT
//   exactly fold against its siblings, the set's arithmetic is unverifiable
//   — render the grouped items with NO headline number rather than a
//   computed total that contradicts a stated one.

import { findExactFold } from "./financialFlow";

export interface CostLikeItem {
  amount: number;
  category: string;
  description?: string;
}

export interface CostsMath {
  // Integer cents to display as the headline, or null when the set's
  // arithmetic doesn't verify (no headline rendered).
  headlineCents: number | null;
  // Index of an item folded as the stated total (counted once via its
  // components), or null.
  foldedTotalIndex: number | null;
  // Per-category sums in integer cents (folded total excluded).
  categoryCents: Array<{ category: string; cents: number }>;
}

const TOTAL_LANGUAGE_RE = /\b(?:total|sum|overall|grand)\b/i;

export function toCents(amount: number): number {
  return Math.round(amount * 100);
}

export function formatCents(cents: number, symbol = "$"): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const rem = String(abs % 100).padStart(2, "0");
  return `${sign}${symbol}${dollars.toLocaleString("en-US")}.${rem}`;
}

// The exact strings the dossier export prints for the Cost summary —
// extracted here so the export surface gets regression coverage without
// rasterizing a PDF. exportDossier.ts consumes these verbatim.
export interface CostSummaryLines {
  // "Total: $269.25 USD" — null when the set's arithmetic doesn't verify.
  totalLine: string | null;
  // Rendered instead of the total when verification fails.
  notice: string | null;
  // One amount string per item, integer-cent formatted.
  amountStrings: string[];
  // Description suffix for a folded stated-total item, by index.
  descriptionSuffix: (index: number) => string;
}

export function buildCostSummaryLines(
  items: CostLikeItem[],
  currencySymbol = "$",
  currency = "",
): CostSummaryLines {
  const math = computeCostsMath(items);
  return {
    totalLine:
      math.headlineCents != null
        ? `Total: ${formatCents(math.headlineCents, currencySymbol)} ${currency}`.trim()
        : null,
    notice:
      math.headlineCents != null
        ? null
        : "Grouped costs (a stated total in this set could not be verified against its items — no computed total shown)",
    amountStrings: items.map((i) => formatCents(toCents(i.amount), currencySymbol)),
    descriptionSuffix: (index: number) =>
      math.foldedTotalIndex === index ? " (stated total — counted once)" : "",
  };
}

export function computeCostsMath(items: CostLikeItem[]): CostsMath {
  const cents = items.map((i) => toCents(i.amount));
  const fold = findExactFold(cents);
  const foldedTotalIndex = fold?.totalIndex ?? null;

  // Unverifiable-total guard: an item that calls itself a total but didn't
  // fold means the stated arithmetic and the listed items disagree.
  const unverifiedTotalClaim = items.some(
    (item, i) =>
      i !== foldedTotalIndex &&
      TOTAL_LANGUAGE_RE.test(item.description ?? "") &&
      // ...unless it IS one of the folded components (a component named
      // "total of X" is the fold total's business, not a claim).
      !(fold?.componentIndices.includes(i) ?? false),
  );

  let headline = 0;
  const byCategory = new Map<string, number>();
  for (let i = 0; i < items.length; i++) {
    if (i === foldedTotalIndex) continue; // counted once via components
    headline += cents[i]!;
    const cat = items[i]!.category;
    byCategory.set(cat, (byCategory.get(cat) ?? 0) + cents[i]!);
  }

  return {
    headlineCents: unverifiedTotalClaim ? null : headline,
    foldedTotalIndex,
    categoryCents: [...byCategory.entries()]
      .map(([category, c]) => ({ category, cents: c }))
      .sort((a, b) => b.cents - a.cents),
  };
}
